import {FormikErrors, FormikTouched, useFormik} from 'formik'
import React, {useState} from 'react'
import {DragDropContext, Draggable, Droppable, DropResult} from 'react-beautiful-dnd'
import {Helmet} from 'react-helmet'
import {Outlet, Route, Routes, useNavigate, useParams} from 'react-router-dom'
import * as Yup from 'yup'
import {ID, KTSVG} from '../../../../_metronic/helpers'
import {PageLink, PageTitle} from '../../../../_metronic/layout/core'
import * as a from '../../../_ancientLibrary/components/'
import {HttpMethod, useAncientRequest} from '../../../_ancientLibrary/utilities'
import {useAuth} from '../../../modules/auth'
import {
  CalculationGroups,
  Calculations,
  CustomReportCarrierModel,
  CustomReportColumns,
  CustomReportDetailModel,
  initialCustomReportCarrierModel,
} from '../../_models/customReportModels'
import {ReportBuilderGroup} from './components/reportBuilderGroup'

const API_URL = process.env.REACT_APP_API_URL

const usersBreadcrumbs: Array<PageLink> = [
  {
    title: 'Home',
    path: '/dashboard',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
  {
    title: 'Custom Reports',
    path: '/c/ReportBuilder',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Report Builder',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

const editReportSchema = Yup.array().of(Yup.object().shape({}))
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

// Main report builder screen for creating and editing custom reports.
const ReportBuilderDetail = () => {
  const navigate = useNavigate()
  const {currentInstanceSettings} = useAuth()
  const {customReportId} = useParams()
  const [report, setReport] = useState<CustomReportCarrierModel>(initialCustomReportCarrierModel)
  const [calculationGroups, setCalculationGroups] = useState<Array<CalculationGroups>>([])
  const [calculations, setCalculations] = useState<Array<Calculations>>([])
  const [calculationsForDropdown, setCalculationsForDropdown] = useState<Array<Calculations>>([])
  const [selectedCalc, setSelectedCalc] = useState<string | undefined>(undefined)
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [calculationOrderByGroup, setCalculationOrderByGroup] = useState<Record<string, Array<ID>>>(
    {}
  )
  const ancientRequest = useAncientRequest()
  const customReportColumnHeaders = currentInstanceSettings
    ? currentInstanceSettings['customReportColumnHeaders' as keyof typeof currentInstanceSettings]
    : []
  const customReportTypes = currentInstanceSettings
    ? currentInstanceSettings['customReportTypes' as keyof typeof currentInstanceSettings]
    : []
  const customReportTypeOptions = currentInstanceSettings
    ? currentInstanceSettings['customReportTypeOptions' as keyof typeof currentInstanceSettings]
    : []
  const measurementSystems = currentInstanceSettings?.measurementSystems || []

  const formik = useFormik({
    initialValues: report,
    validationSchema: editReportSchema,
    enableReinitialize: true,
    validateOnChange: false,
    onSubmit: async (values: any) => {
      // Filter reportDetails to include only those where isSelected is true
      const selectedReportDetails = values.reportDetails.filter(
        (detail: {isSelected: any}) => detail.isSelected
      )

      // Create a new object with the filtered reportDetails
      const payload = {
        ...values,
        reportDetails: selectedReportDetails,
      }

      ancientRequest(HttpMethod.POST, `/bm/ReportBuilder/SaveReport/`, payload, 'Report saved.').then(
        (customReportId) => {
          navigate(`/c/reportBuilder/report/` + customReportId.data)
        }
      )
    },
  })

  // Loads calculation options for the report dropdown selector.
  const loadCalculations = async () => {
    var calculations = await ancientRequest(
        HttpMethod.GET,
      `/bm/ReportBuilder/GetCalculationsForDropdown/6aba8918-711c-40e0-ab9d-bcbdddd5e29f`
    )
    if (calculations) {
      setCalculationsForDropdown(calculations.data)
    }
  }

  // Loads the report, groups, and calculations needed to populate the builder UI.
  const loadReport = async (nextCustomReportId?: string) => {
    if (!nextCustomReportId) return

    setIsLoaded(false)
    setCalculationOrderByGroup({})

    ancientRequest(HttpMethod.GET, `/bm/ReportBuilder/GetReport/${nextCustomReportId}/`).then(
      (reportResponse) => {
        ancientRequest(HttpMethod.GET, `/bm/ReportBuilder/getCalculationGroups/`).then((groupResponse) => {
          setCalculationGroups(groupResponse.data)

          ancientRequest(HttpMethod.GET, `/bm/ReportBuilder/getCalculations/`).then((calculationResponse) => {
            setCalculations(calculationResponse.data)

            calculationResponse.data.forEach((calculation: Calculations) => {
              // Check if the calculationID is already present in reportDetails
              const existingDetail = reportResponse.data.reportDetails.find(
                (detail: CustomReportDetailModel) =>
                  detail.calculationID === calculation.calculationID
              )

              // If the calculation is not already in reportDetails, add it
              if (!existingDetail) {
                // Create a new CustomReportDetailModel based on the calculation
                const newDetail: CustomReportDetailModel = {
                  customReportDetailID: reportResponse.data.report.customReportID, // You may need to implement a function to generate unique IDs
                  calculationID: calculation.calculationID,
                  isSelected: false, // You can set this based on your requirements
                  sort: calculation.sort ?? null,
                }

                // Add the new detail to reportDetails
                reportResponse.data.reportDetails.push(newDetail)
              }
            })

            setReport(reportResponse.data)
          })
        })
      }
    )
  }

  React.useEffect(() => {
    loadCalculations()
  }, [])

  React.useEffect(() => {
    loadReport(customReportId)
  }, [customReportId])
  React.useEffect(() => {
    if (report && report.report.customReportID !== null) {
      setIsLoaded(true)
    }
  }, [report])

  //Create a method that filters calculations by group.filterGroupID
  // Filters calculations to those belonging to a specific group.
  const filterCalculations = (groupID: ID) => {
    return calculations.filter((calculation) => calculation.groupID === groupID)
  }

  // Normalizes a group ID into a stable string key.
  const getGroupKey = (groupId: ID) => String(groupId ?? '')

  const getCalculationMeasurementSystemBadge = (measurementSystem?: string | null) => {
    const normalizedMeasurementSystem = measurementSystem?.toLowerCase()
    if (!normalizedMeasurementSystem || normalizedMeasurementSystem === EMPTY_GUID) return null

    const system = measurementSystems.find(
      (setting) => String(setting.id).toLowerCase() === normalizedMeasurementSystem
    )

    if (system?.settingName === 'Imperial') return 'IMPERIAL SYSTEM ONLY'
    if (system?.settingName === 'Metric') return 'METRIC SYSTEM ONLY'
    return null
  }

  // Returns calculations for a group ordered by the current drag order.
  const getOrderedCalculations = (groupId: ID) => {
    const groupCalculations = filterCalculations(groupId)
    const groupKey = getGroupKey(groupId)
    if (!groupKey) return groupCalculations

    const order = calculationOrderByGroup[groupKey] || []
    if (order.length === 0) return groupCalculations

    const orderIndex = new Map<ID, number>()
    order.forEach((id: ID, index: number) => orderIndex.set(id, index))
    return [...groupCalculations].sort((a, b) => {
      const aIndex = orderIndex.get(a.calculationID)
      const bIndex = orderIndex.get(b.calculationID)
      if (aIndex == null && bIndex == null) return 0
      if (aIndex == null) return 1
      if (bIndex == null) return -1
      return aIndex - bIndex
    })
  }

  // Reassigns sort values for selected calculations based on the current order.
  const updateGroupSorts = (orderedIds: Array<ID>) => {
    const nextDetails = [...formik.values.reportDetails]
    let selectedSort = 1

    orderedIds.forEach((calcId) => {
      const detailIndex = nextDetails.findIndex((detail) => detail.calculationID === calcId)
      if (detailIndex === -1) return

      const detail = nextDetails[detailIndex]
      if (detail.isSelected) {
        nextDetails[detailIndex] = {
          ...detail,
          sort: selectedSort++,
        }
      } else {
        nextDetails[detailIndex] = {
          ...detail,
          sort: null,
        }
      }
    })

    formik.setFieldValue('reportDetails', nextDetails, false)
  }

  // Returns the ordered calculation IDs for a group, falling back to default order.
  const getGroupOrderIds = (groupId: ID) => {
    const groupKey = getGroupKey(groupId)
    if (!groupKey) return filterCalculations(groupId).map((c) => c.calculationID)
    return (
      calculationOrderByGroup[groupKey] ||
      filterCalculations(groupId).map((c) => c.calculationID)
    )
  }

  // Handles checkbox toggles and recalculates sort for selected items in the group.
  const handleCalculationToggle = (detailIndex: number, groupId: ID, nextSelected: boolean) => {
    const orderedIds = getGroupOrderIds(groupId)
    const nextDetails = [...formik.values.reportDetails]
    if (nextDetails[detailIndex]) {
      nextDetails[detailIndex] = {
        ...nextDetails[detailIndex],
        isSelected: nextSelected,
      }
    }

    let selectedSort = 1
    orderedIds.forEach((calcId) => {
      const index = nextDetails.findIndex((detail) => detail.calculationID === calcId)
      if (index === -1) return
      const detail = nextDetails[index]
      if (detail.isSelected) {
        nextDetails[index] = {
          ...detail,
          sort: selectedSort++,
        }
      } else {
        nextDetails[index] = {
          ...detail,
          sort: null,
        }
      }
    })

    formik.setFieldValue('reportDetails', nextDetails, false)
  }

  // Handles drag-and-drop reordering within a group and updates sort values.
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return
    if (result.source.droppableId !== result.destination.droppableId) return

    const groupKey = result.source.droppableId
    const group = calculationGroups.find(
      (entry) => getGroupKey(entry.filterGroupID) === groupKey
    )
    if (!group) return

    const currentOrder =
      calculationOrderByGroup[groupKey] ||
      filterCalculations(group.filterGroupID).map((calculation) => calculation.calculationID)

    const reordered = [...currentOrder]
    const [removed] = reordered.splice(result.source.index, 1)
    reordered.splice(result.destination.index, 0, removed)

    setCalculationOrderByGroup((prev) => ({
      ...prev,
      [groupKey]: reordered,
    }))
    updateGroupSorts(reordered)
  }

  // Initializes per-group calculation order from persisted sort values.
  React.useEffect(() => {
    if (!isLoaded || calculationGroups.length === 0) return

    setCalculationOrderByGroup((prev) => {
      const next = {...prev}
      calculationGroups.forEach((group) => {
        const key = getGroupKey(group.filterGroupID)
        if (!key) return
        if (!next[key] || next[key].length === 0) {
          const groupCalculations = filterCalculations(group.filterGroupID)
          const groupIds = groupCalculations.map((c) => c.calculationID)

          const sortedDetailIds = report.reportDetails
            .filter(
              (detail) =>
                detail.sort != null && groupIds.includes(detail.calculationID)
            )
            .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
            .map((detail) => detail.calculationID)

          const remainingIds = groupIds.filter((id) => !sortedDetailIds.includes(id))
          next[key] = [...sortedDetailIds, ...remainingIds]
        }
      })
      return next
    })
  }, [isLoaded, calculationGroups, calculations, report.reportDetails])

  // Renders the report type selector radio buttons.
  const customReportTypeSelector = () => {
    return (
      isLoaded && (
        <>
          <div className='row'>
            {customReportTypes?.map((reportType, typeIndex) => {
              return (
                <div className='col-md-2' key={typeIndex}>
                  <a.AncientInputRadioJsonArray
                    displayText={reportType.settingName}
                    fieldProps={formik.getFieldProps('report.reportType')}
                    id={`type-${typeIndex.toString()}`}
                    value={reportType.settingName}
                    key={typeIndex}
                  ></a.AncientInputRadioJsonArray>
                </div>
              )
            })}
          </div>
        </>
      )
    )
  }

  // Renders the report column selection checkboxes.
  const customReportColumnHeadersCheckboxes = () => {
    return (
      isLoaded && (
        <>
          <div className='row'>
            {customReportColumnHeaders?.map((columnHeader, columnIndex) => {
              //check if columnHeader.Id has a match in customReportTypeOptions.CustomReportTypeId
              if (
                !customReportTypeOptions?.find(
                  (typeOption) =>
                    typeOption.settingName === formik.values.report.reportType &&
                    typeOption.customReportColumnHeaderId === columnHeader.id
                )
              )
                return

              //find the index of formik.reportDetails by calculationID
              const detailIndex = report.reportColumns.findIndex(
                (detail) => detail.reportColumnID === columnHeader.migrationReportColumnID
              )
              // Use type assertion to tell TypeScript that formik.errors.reportDetails[detailIndex] is of type FormikErrors<CustomReportDetailModel>
              const fieldError =
                formik.errors.reportColumns &&
                (formik.errors.reportColumns[detailIndex] as FormikErrors<CustomReportDetailModel>)
                  ?.isSelected
              const fieldTouched =
                formik.touched.reportColumns &&
                (
                  formik.touched.reportColumns[
                    detailIndex
                  ] as FormikTouched<CustomReportDetailModel>
                )?.isSelected

              return (
                <div className='col-md-2' key={columnIndex}>
                  <a.AncientInputCheckbox
                    displayText={columnHeader.settingDescription}
                    fieldProps={formik.getFieldProps(`reportColumns.${detailIndex}.visible`)}
                    touched={fieldTouched}
                    errors={fieldError}
                    id={`column-${columnIndex.toString()}`}
                    key={columnIndex}
                  ></a.AncientInputCheckbox>
                </div>
              )
            })}
          </div>
        </>
      )
    )
  }

  // Renders calculation groups with draggable rows and selection checkboxes.
  const customReportCalculations = () => {
    return (
      isLoaded && (
        <DragDropContext onDragEnd={onDragEnd}>
          {calculationGroups?.map((group, groupIndex) => {
            const groupKey = getGroupKey(group.filterGroupID)
            return (
              <ReportBuilderGroup
                groupName={group.filterName}
                groupId={group.filterGroupID}
                key={groupIndex}
              >
                <Droppable droppableId={groupKey}>
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {isLoaded &&
                        getOrderedCalculations(group.filterGroupID)?.map(
                          (calculation, calcIndex) => {
                            const detailIndex = report.reportDetails.findIndex(
                              (detail) => detail.calculationID === calculation.calculationID
                            )
                            const fieldError =
                              formik.errors.reportDetails &&
                              (
                                formik.errors.reportDetails[
                                  detailIndex
                                ] as FormikErrors<CustomReportDetailModel>
                              )?.isSelected
                            const fieldTouched =
                              formik.touched.reportDetails &&
                              (
                                formik.touched.reportDetails[
                                  detailIndex
                                ] as FormikTouched<CustomReportDetailModel>
                              )?.isSelected
                            const measurementSystemBadge =
                              getCalculationMeasurementSystemBadge(calculation.measurementSystem)

                            return (
                              <Draggable
                                key={calculation.calculationID}
                                draggableId={String(calculation.calculationID)}
                                index={calcIndex}
                              >
                                {(draggableProvided) => (
                                    <div
                                        className='d-flex align-items-center w-100 mb-2'
                                        ref={draggableProvided.innerRef}
                                        {...draggableProvided.draggableProps}
                                    >
                                      <div className='d-flex align-items-center flex-grow-1'>
                                        <a.AncientInputCheckbox
                                            displayText={`${calculation.displayName} (${calculation.measurementLabel})`}
                                            fieldProps={{
                                              ...formik.getFieldProps(
                                                  `reportDetails.${detailIndex}.isSelected`
                                              ),
                                              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                                                  handleCalculationToggle(
                                                      detailIndex,
                                                      group.filterGroupID,
                                                      event.target.checked
                                                  ),
                                            }}
                                            touched={fieldTouched}
                                            errors={fieldError}
                                            id={`calc-${calcIndex.toString()}`}
                                        ></a.AncientInputCheckbox>
                                        {measurementSystemBadge && (
                                            <span className='badge badge-light-danger fw-bolder'>
                                              {measurementSystemBadge}
                                            </span>
                                        )}
                                      </div>
                                      <div
                                          className='ms-2 text-muted'
                                          title='Drag to reorder'
                                          {...draggableProvided.dragHandleProps}
                                      >
                                        <i className="fas fa-bars fa-lg"></i>
                                      </div>
                                    </div>
                                )}
                              </Draggable>
                            )
                          }
                        )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </ReportBuilderGroup>
            )
          })}
        </DragDropContext>
      )
    )
  }

  // Renders the overall report builder form UI.
  const reportBuilderForm = () => {
    return (
      isLoaded && (
        <>
          <Helmet>
            <title>Report Builder</title>
          </Helmet>
          <PageTitle breadcrumbs={usersBreadcrumbs}>Report Builder</PageTitle>

          <form id='report_builder_form' className='form' onSubmit={formik.handleSubmit} noValidate>
            <a.AncientCard>
              <a.AncientCardHeader>
                <span>
                  Report Builder
                </span>
                <div>
                  <a.AncientButton
                      isFormSubmit={true}
                      displayText='Save Report'
                      icon='/media/icons/duotune/files/fil008.svg'
                  />
                </div>
              </a.AncientCardHeader>
              <a.AncientCardBody>
                <h2>Report Setup</h2>
                <a.AncientInputText
                  displayText='Report Name'
                  verticalLabel={true}
                  fieldProps={formik.getFieldProps('report.reportName')}
                  touched={formik.touched.report?.reportName}
                  errors={formik.errors.report?.reportName}
                />

                <h2>Report Type</h2>
                {customReportTypeSelector()}

                <h2>Report Columns</h2>
                {customReportColumnHeadersCheckboxes()}

                <h2>Admin Options</h2>
                <a.AncientInputCheckbox
                  displayText='Standard Reports (Read-Only)'
                  fieldProps={formik.getFieldProps('report.isStandardReport')}
                  touched={formik.touched.report?.isStandardReport}
                  errors={formik.errors.report?.isStandardReport}
                  id='standard-report'
                ></a.AncientInputCheckbox>

                {customReportTypes?.find(
                  (detail: any) => detail.settingName === formik.values.report.reportType
                )?.showCalculationComparison && (
                  <a.AncientDropdown
                    id={'reportCalculation'}
                    fieldProps={formik.getFieldProps(`report.selectedCalc`)}
                    dropdownData={calculationsForDropdown}
                    verticalLabel={true}
                    selectedValue={formik.values.report.selectedCalc}
                    optionKey='settingName'
                    optionLabel='settingDescription'
                    optionGroup='reportGroup'
                    optionGroupSort='reportGroupSort'
                    displayText='Calculation'
                  />
                )}

                <h2>Calculations</h2>
                {customReportCalculations()}
              </a.AncientCardBody>
            </a.AncientCard>
          </form>
        </>
      )
    )
  }

  return (
    <Routes>
      <Route element={<Outlet />}>
        <Route path='*' element={reportBuilderForm()} />
      </Route>
    </Routes>
  )
}

export default ReportBuilderDetail
